import builderIoService from '../composables/builderIoService';
import { useLocaleStore } from '../stores/localeStore';
import { URL_CONFIG } from '../../shared/utils/url.const.js'
import { CONTENT_TYPES } from '../../shared/utils/contentTypes'
import { extractTextFromHtml } from "#shared/utils/common";
import { useRoute } from "vue-router";
import { computed } from 'vue'


type EntityType = 'articles' | 'videos' | 'podcasts' | 'mixed';

const toSingular = (w: string) => (w.endsWith("s") ? w.slice(0, -1) : w);

export const useTakeFiveDaily = (entityType?: EntityType) => {
    const service = builderIoService();
    const route = useRoute();
    const localeStore = useLocaleStore();

    const limit = 10;

    const sort = { 'data.publishAt': -1 as const };

    const currentPage = computed(() => {
        const pageParam = route.query.page;
        const page = pageParam ? Number.parseInt(pageParam as string) : 1;
        return page > 0 ? page : 1;
    });

    const searchQuery = computed(() => {
        if (!entityType || entityType === 'mixed') {
            return (route.query.search as string) || '';
        }
        return '';
    });

    const tagTitle = computed(() => {
        const t = route.query.t;
        return Array.isArray(t) ? t[0] : t || "";
    });

    const countryCode = computed(() => {
        const code = (localeStore.locale as any)?.country?.code;
        return typeof code === 'string' && code.trim() ? code : 'US';
    });

    const apiType = computed(() => (!entityType || entityType === 'mixed') ? 'all' : entityType);

    const transformItem = (item: any, type?: string) => {
        const itemType = type || item.data?.entityType || 'articles';

        const typeMap: Record<string, { path: string; type: 'article' | 'video' | 'podcast' }> = {
            articles: { path: 'articles', type: 'article' },
            videos: { path: 'videos', type: 'video' },
            podcasts: { path: 'podcasts', type: 'podcast' }
        };

        const mapped = typeMap[itemType] || typeMap.articles;

        const defaultImage = new URL('@/assets/images/default-podcast.png', import.meta.url).href;

        const getImage = () => {
            if (mapped!.type === 'podcast') {
                return defaultImage;
            }

            return item.data?.media?.medFile
                || item.data?.cau?.value?.data?.cauAvatarMedia?.medFile;
        }

        const stripHtml = (html: string = ""): string => { return extractTextFromHtml(html); };

        const getExcerpt = () => {
            const d = item?.data;

            if (mapped!.type === 'article') {
                return stripHtml(d?.content || '');
            }
            return stripHtml(d?.description || d?.metaDescription || '');
        };

        return {
            title: item.data?.name || item.data?.metaTitle || '',
            url: `${URL_CONFIG.TAKE_5_DAILY}/${toSingular(mapped!.path)}/${item.data?.urlKey || ''}`,
            image: getImage(),
            excerpt: getExcerpt(),
            publishAt: item.data?.publishAt || '',
            authorName: item.data?.cau?.value?.data?.cauName || 'Unknown Author',
            type: mapped!.type,
            audioLink: item.data?.audioLink || '',
            videoLink: item.data?.videoLink || '',
            takeFiveId: item?.id || ''
        };
    };

    const { data: itemsData, error, pending, refresh } = useAsyncData(
        `take-five-${entityType || 'all'}-page-${currentPage.value}-search-${searchQuery.value}-tag-${tagTitle.value}-loc-${countryCode.value}`,
        async () => {
            const offset = (currentPage.value - 1) * limit;

            const code = (countryCode.value || 'US').trim();

            const builderQuery: any = {
                $and: [
                    {
                        $or: [
                            { 'data.countryRestrictionsWhitelist': { $size: 0 } },
                            { 'data.countryRestrictionsWhitelist': { $exists: false } },
                            { 'data.countryRestrictionsWhitelist.countryCode': { $in: [code] } }
                        ]
                    },
                    {
                        'data.countryRestrictionsBlacklist.countryCode': { $nin: [code] }
                    }
                ],
                'data.status': 'Active'
            };

            if (entityType && entityType !== 'mixed') {
                builderQuery['data.entityType'] = entityType;
            }

            const term = searchQuery.value?.trim();
            if ((!entityType || entityType === 'mixed') && term) {
                builderQuery['data.name'] = {
                    $regex: term,
                    $options: 'i'
                };
            }

            if (tagTitle.value) {
                builderQuery['data.taggings'] = {
                    $elemMatch: {
                        tag: tagTitle.value
                    }
                };
            }

            const result = await service.fetchManyModels(CONTENT_TYPES.TAKE_FIVE_DAILY, undefined, false, {
                limit: limit + 1,
                offset: offset,
                sort: sort,
                query: builderQuery
            });

            const transformed = (result || []).map((item: any) => transformItem(item, entityType));
            const items = transformed.slice(0, limit);
            const hasNextPage = transformed.length > limit;

            return { items, hasNext: hasNextPage };
        },
        {
            server: true,
            lazy: false,
            watch: [currentPage, searchQuery, tagTitle],
            getCachedData: () => undefined
        }
    );

    const items = computed(() => itemsData?.value?.items || []);
    const hasNextFromItems = computed(() => !!itemsData?.value?.hasNext);
    const hasPrev = computed(() => currentPage?.value > 1);

    const { data: countData } = useAsyncData(
        `t5d-count-type-${apiType.value}-tag-${tagTitle.value}-loc-${countryCode.value}`,
        async () => {
            try {
                const params: Record<string, any> = {
                    format: 'json',
                    type: apiType.value,
                    countryCode: countryCode.value
                };
                if (tagTitle.value) params.tag = tagTitle.value;

                const runtimeConfig = useRuntimeConfig();
                const url = `${runtimeConfig.public.thorneSite}/${URL_CONFIG.API_PAGINATION_COUNT}`;

                const res = await $fetch<any>(url, { params });
                let totalCount = 0;
                if (typeof res === 'number') {
                    totalCount = res;
                } else if (typeof res?.count === 'number') {
                    totalCount = res.count;
                } else if (typeof res?.totalCount === 'number') {
                    totalCount = res.totalCount;
                }
                return { 
                    totalCount 
                };
            } catch (e) {
                console.error("Request failed with err: ", e)
                return { totalCount: 0 };
            }
        },
        {
            server: true,
            lazy: false,
            watch: [apiType, tagTitle, countryCode]
        }
    );

    const totalCount = computed(() => countData?.value?.totalCount ?? null);
    const totalPages = computed(() =>{
        if(totalCount.value != null) {
            return Math.max(1, Math.ceil(totalCount.value / limit))
        }
        return undefined
    });

    const isSearching = computed(() => {
        const term = (searchQuery.value || '').trim();
        const hasTag = !!(tagTitle.value && String(tagTitle.value).trim());
        return term.length > 0 || hasTag;
    });

    const hasNext = computed(() => {
        if (!isSearching.value && totalPages.value != null) {
            return currentPage.value < totalPages.value;
        }

        if (isSearching.value) {
            return items.value.length === limit ? hasNextFromItems.value : false;
        }
        return hasNextFromItems.value;
    });

    const primary = computed(() =>
        items.value && items.value.length > 0 ? items.value[0] : null
    );

    const secondary = computed(() =>
        items.value && items.value.length > 1 ? items.value.slice(1, 3) : []
    );

    const tertiary = computed(() =>
        items.value && items.value.length > 3 ? items.value.slice(3) : []
    );

    return {
        data: items,
        error,
        pending,
        refresh,
        primary,
        secondary,
        tertiary,
        currentPage,
        searchQuery,
        limit,
        hasNext,
        hasPrev,
        totalCount,
        totalPages
    };
};
